import { useEffect, useRef, useState, useTransition } from 'react'
import { useHistory, useParams } from 'react-router'
import publisherApi from 'src/component/app/publisher/PublisherApi'
import { DspBean, LanguageBean, PublisherBean, TimeZoneBean, CountryBean } from '../../../api/api'
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader'
import { InputCSV } from './inputCSV/InputCSV'
import { Alert, Button, Checkbox, Form, Input, InputNumber, Modal, notification, Segmented, Select, Table, TreeSelect, Upload } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import adTagJsScript from '../../../adTagJsScript';
import { env } from '../../../env';
import { RevenueCalculationMethod, SellerType, getIabCategoriesAsTree, resolveIabDefaultsChange, stripSelectDefaultsValue, truncateErrorMessage } from '../../../util/Util';

import { CheckCircleOutlined, StopOutlined, CloseOutlined, PlusOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import {StorageKey, storageReaderFactory, storageWriterFactory} from '../../../util/Storage'
import ChangeLogHistoryModal from '../changeHistory/changeLogHistoryModal'
import { PUBLISHER_EXCLUDED_FIELDS, PUBLISHER_FIELD_LABELS } from '../../../util/changeLogFieldLabels'
import PublisherDomainListCard from './PublisherDomainListCard'
const FormItem = Form.Item
const Option = Select.Option

const formInfoItems: FormDataType[] = [
    FormDataType.DspList,
    FormDataType.IabCategories,
    FormDataType.Languages,
    FormDataType.TimeZones,
    FormDataType.Publishers,
    FormDataType.Countries
]

const WinNotificationEnum = DspBean.WinNotificationEnum

export const AD_SERVER_URL = 'AD_SERVER_URL'
export const PUBLISHER_TOKEN = 'PUBLISHER_TOKEN'

const PublisherEdit = () => {

    const [info, setInfo] = useState({
        dspList: [],
        iabCategories: [],
        languages: [],
        timezones: [],
        publishers: [],
        countries: [],
    })

    const handleCountrySelect = (value: number | string) => {
        const allIds = (info.countries ?? [])
            .map((c: CountryBean) => c.id)
            .filter((v): v is number => v !== undefined && v !== null)

        if (value === 'All') {
            form.setFieldsValue({ countries: ['All', ...allIds] })
        } else {
            const current: (number | string)[] = form.getFieldValue('countries') ?? []
            const selected = Array.from(new Set([...current.filter((v) => v !== 'All'), value]))
            if (selected.length === allIds.length) {
                form.setFieldsValue({ countries: ['All', ...selected] })
            }
        }
    }

    const handleCountryDeselect = (value: number | string) => {
        if (value === 'All') {
            form.setFieldsValue({ countries: [] })
        } else {
            const current: (number | string)[] = form.getFieldValue('countries') ?? []
            form.setFieldsValue({ countries: current.filter((v) => v !== 'All' && v !== value) })
        }
    }

    const history = useHistory()
    const { id } = useParams<{ id?: string }>()
    const publisherId = Number(id);

    const [token, setToken] = useState(undefined);
    const [error, setError] = useState(undefined);  
    const [validation, setValidation] = useState(false);
    const [formLoaded, setFormLoaded] = useState(false);
    const [useParentSellerAccountId, setuseParentSellerAccountId] = useState(null);
    const [parentPublisherId, setParentPublisherId] = useState(undefined);
    const [passThrough, setpassThrough] = useState(false);
    const [includeDomains, setIncludeDomains] = useState(false);
    const [form] = Form.useForm();
    const [saveMode, setSaveMode] = useState<'DRAFT' | 'SAVE'>('SAVE');
    const [status, setStatus] = useState<string>();
    const iabPrevRef = useRef<number[]>([])

    const [allClassification, setAllClassification] = useState(false)
    const [acceptAllMobileAppIds, setAcceptAllMobileAppIds] = useState(false)
    const [domainMobileError, setDomainMobileError] = useState('')

    // ─── Domains state ──────────────────────────────────────────────────────
    const [domains, setDomains] = useState<string[]>([])
    const [domainsCollapsed, setDomainsCollapsed] = useState(false)
    const [domainsFileLabel, setDomainsFileLabel] = useState<string | null>(null)

    // ─── Mobile App IDs state ───────────────────────────────────────────────
    const [mobileAppIds, setMobileAppIds] = useState<string[]>([])
    const [mobileAppsCollapsed, setMobileAppsCollapsed] = useState(false)
    const [mobileAppIdsFileLabel, setMobileAppIdsFileLabel] = useState<string | null>(null)

    const navigateToPublisherList = () => {
        history.push('/publishers', { fromPublisherList: true });
    };

    const validateDomainsOrMobileApps = (): boolean => {
        if (allClassification || acceptAllMobileAppIds) {
            setDomainMobileError('')
            return true
        }
        if (domains.length === 0 && mobileAppIds.length === 0) {
            setDomainMobileError('Either Domains or Mobile App IDs is required')
            return false
        }
        setDomainMobileError('')
        return true
    }

    const handleSubmit = () => {

        // 🟡 SAVE AS DRAFT → validate ONLY name
        if (saveMode === 'DRAFT') {
          form
            .validateFields(['name'])
            .then(() => {
              const allValues = form.getFieldsValue(true); // Get all form values including defaults
              submitPublisher(allValues, true, false);
            })
            .catch(() => {});
          return;
        }

        // 🟢 DRAFT → ACTIVATE
        if (status === 'DRAFT') {
            form
            .validateFields()
            .then(() => {
              if (!validateDomainsOrMobileApps()) return;
              const allValues = form.getFieldsValue(true); // Get all form values including defaults
              submitPublisher(allValues, false, true);
            })
            .catch(() => {});
            return;
        }

        // 🟢 ACTIVE or DEACTIVATED → SAVE only
        form
            .validateFields()
            .then(() => {
              if (!validateDomainsOrMobileApps()) return;
              const allValues = form.getFieldsValue(true); // Get all form values including defaults
              submitPublisher(allValues, false, false);
            })
            .catch(() => {});
        };

    const submitPublisher = (values: any, isDraft: boolean, shouldActivate: boolean) => {
        const domainList: string[] = allClassification ? [] : domains
        const mobileAppIdList: string[] = acceptAllMobileAppIds ? [] : mobileAppIds
        const countries = values?.countries
            ?.filter((countryId: number | string) => countryId !== 'All')
            .map((countryId: number) => ({ id: countryId })) || [];
        delete values.blackListDomainsField;

        publisherApi
        .edit(id, {
            ...values,
            iabCategoriesIds: stripSelectDefaultsValue(values.iabCategoriesIds),
            domains: domainList,
            acceptAllDomains: allClassification,
            mobileAppIds: mobileAppIdList,
            acceptAllMobileAppIds,
            countries,
            validation: validation || false,
            useParentSellerAccountId,
            passThrough,
            ...(isDraft && { status: 'DRAFT' }),
        })
        .then(() => {
            if (isDraft) {
                Modal.info({
                    title: 'Publisher saved as Draft',
                    content:
                    'This publisher is in Draft status. Activate it to enable bidding and generate API credentials.',
                    onOk: () => navigateToPublisherList(),
                });
                return;
                }

                if (shouldActivate) {
                activatePublisher();
                return;
                }

                // ✅ ACTIVE / DEACTIVATED save
                Modal.success({
                title: 'Publisher updated',
                onOk: () => navigateToPublisherList(),
                });
            })
            .catch(onRejected);
        };

      const activatePublisher = () => {
        publisherApi
          .activate(Number(id))
          .then(response => {
            if (response?.data?.authDataBean) {
              userCredentials(response.data.authDataBean);
            } else {
              navigateToPublisherList();
            }
          })
          .catch(err => {
            Modal.error({
              title: 'Activation Failed',
              content: err?.error || 'Unable to activate publisher',
            });
          });
      };

      const userCredentials = (authDataBean) => {
        Modal.info({
            title: 'New account created',
            content: <div>
                <br />
                <div>Please save account credentials</div>
                <br />
                <div>Username: {authDataBean.username}</div>
                <div>Password: {authDataBean.password}</div>
            </div>,
            okText: 'OK',
            okCancel: false,
            onOk: () => navigateToPublisherList()
        });
    };

    const onRejected = err => {
        setError(truncateErrorMessage(err?.error))
    }

    const loadData = async () => {
        try {
            let response: any = await publisherApi.view(id)
            setuseParentSellerAccountId(response?.data?.useParentSellerAccountId)
            setpassThrough(response?.data?.passThrough)
            setIncludeDomains(response?.data?.includeDomains)

            if (response?.data?.acceptAllDomains) {
                setAllClassification(true)
            }
            if (response?.data?.acceptAllMobileAppIds) {
                setAcceptAllMobileAppIds(true)
            }

            // Pre-populate domains
            if (Array.isArray(response.data?.domains) && response.data.domains.length > 0) {
                const loadedDomains = response.data.domains
                setDomains(loadedDomains)
                setDomainsFileLabel('domains.csv')
            }

            // Pre-populate mobileAppIds
            if (Array.isArray(response.data?.mobileAppIds) && response.data.mobileAppIds.length > 0) {
                const loadedMobileAppIds = response.data.mobileAppIds
                setMobileAppIds(loadedMobileAppIds)
                setMobileAppIdsFileLabel('mobileAppIds.csv')
            }

            let formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)

            // Transform countries array from objects to IDs
            // domains and mobileAppIds are already plain string[] from the API
            const savedCountryIds = response.data.countries?.map((country: CountryBean) => country.id) || []
            const allCountryIds = ((formInfo as any)?.countries ?? [])
                .map((c: CountryBean) => c.id)
                .filter((v: number | undefined) => v !== undefined && v !== null)
            const formData = {
                ...response.data,
                countries:
                    allCountryIds.length > 0 && savedCountryIds.length === allCountryIds.length
                        ? ['All', ...savedCountryIds]
                        : savedCountryIds,
            }

            form.setFieldsValue(formData)
            setStatus(response?.data?.status);
            if(response?.data?.parentPublisherId){
                setParentPublisherId(response?.data?.parentPublisherId)
            }
            setToken(response.data.token)
            // formLoaded = true gates InputCSV render so it reads the pre-filled form values on mount
            setFormLoaded(true)
        } catch (e) {
            onRejected(e)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleTokenGenerator = () => {
        Modal.confirm({
            title: 'Generate new token',
            content: 'Do you want generate new publisher token?',
            okText: 'Yes',
            cancelText: 'NO',
            onOk: async () => {
                try {
                    let response: any = await publisherApi.generateToken(Number.parseInt(id ?? '-1'))
                    setToken(response.data.token)
                } catch (e) {
                    onRejected(e)
                }
            }
        });
    };

    const handleSelectAll = (value: any) => {
        if (value && value.length && value.includes('__all')) {
            if (value.length === info.dspList.length + 1) {
                return []
            }
            return [...(info.dspList.map((el: DspBean) => el.id))]
        }
        return value
    }

    const handleValidation = (value) => {
        setValidation(value.target.checked)
    };
    const handleuseParentSellerAccountId = (value) => {
        setuseParentSellerAccountId(value.target.checked)
    };
    const handleParentPublisherId =(event)=>{
        setParentPublisherId(event)
    }
    const handlepassThrough = (value) => {
        setpassThrough(value.target.checked)
    };
    const handleIncludeDomains = (value) => {
        setIncludeDomains(value.target.checked)
    };
    const generateCode = () => {
        Modal.info({
            width: 1024,
            title: 'Paste this code on your site:',
            content: <TextArea rows={30} value={scriptGenerator()} style={{ fontSize: 13 }} />,
            okText: 'Close',
        });
    }

    const scriptGenerator = () => {

        return replaceTagVars(adTagJsScript)
    }

    const replaceTagVars = (scriptBody) => scriptBody
        .replace(AD_SERVER_URL, env.adServerUrl)
        .replace(PUBLISHER_TOKEN, token)

    const onChangeIab = (value: number[]) => {
        const next = resolveIabDefaultsChange(value || [], iabPrevRef.current, info.iabCategories as any)
        iabPrevRef.current = next
        form.setFieldsValue({ ['iabCategoriesIds']: next })
        return next
    }

    const handleDownload = async () => {
        try {
            const textContent = await publisherApi.downloadTxtFile(publisherId);
            if (!textContent) {
                console.log('No content received');
                return;
            }

            const blob = new Blob([textContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `publisher_${publisherId}.ads.txt`;
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleDownloadAppAds = async () => {
        try {
            const textContent = await publisherApi.downloadAppAdsTxtFile(publisherId);
            if (!textContent) {
                console.log('No content received');
                return;
            }
    
            const blob = new Blob([textContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `publisher_${publisherId}.app-ads.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
    
        } catch (error: any) {
            console.error('Error downloading app-ads file:', error);
    
            const message =
                error?.error ||           // ← plain text from BE via our fix above
                error?.response?.data?.message ||
                error?.message ||
                '500 Internal Server Error';
    
            notification.error({
                message: 'Download Failed',
                description: message,
                duration: 5,
                placement: 'top',
                style: { width: 600 },
            });
        }
    };

    useEffect(() => {
        if (!useParentSellerAccountId) {
          form.setFields([{ name: "parentPublisherId", errors: [] }]);
        }
    }, [useParentSellerAccountId]);

    // ─── Domain handlers ────────────────────────────────────────────────────
    const handleAddDomain = (value: string) => setDomains(prev => [...prev, value])
    const handleUploadDomains = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setDomains(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveDomain = (index: number) =>
        setDomains(prev => prev.filter((_, i) => i !== index))
    const handleClearAllDomains = () => setDomains([])

    // ─── Mobile App ID handlers ─────────────────────────────────────────────
    const handleAddMobileAppId = (value: string) => setMobileAppIds(prev => [...prev, value])
    const handleUploadMobileAppIds = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setMobileAppIds(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveMobileAppId = (index: number) =>
        setMobileAppIds(prev => prev.filter((_, i) => i !== index))
    const handleClearAllMobileAppIds = () => setMobileAppIds([])

    return (
        <div>
            {error && <div><Alert message={error} type="error" /><br /></div>}
            <div className='flex justify-end gap-2 mb-4'>
            <ChangeLogHistoryModal entityType="PUBLISHER" entityId={publisherId} fieldLabelMap={PUBLISHER_FIELD_LABELS} excludeFields={PUBLISHER_EXCLUDED_FIELDS} />

            <Button style={{
                width: '15%',
            }} type="default" htmlType="button"
            onClick={handleDownloadAppAds}
             >
                Download App-Ads.txt
                    </Button>

            <Button style={{
                width: '15%',
            }} type="default" htmlType="button"
            onClick={handleDownload}
             >
                Download Ads.txt
                    </Button>
            </div>
            <Form
                style={{
                    maxWidth: '1000px'
                }}
                onFinish={handleSubmit}
                form={form}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
            >
                <FormItem
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: 'Please input Publisher name'
                        },
                        {
                            max: 255,
                            message: 'Name cannot be longer that 255 characters'
                        }
                    ]}
                    label="Name"
                    tooltip={'Name of the publisher'}
                >
                    <Input />
                </FormItem>
                <FormItem
                    name="sellerAccountId"
                    rules={[
                        {
                            type: 'integer',
                            min: 1,
                            max: 9999,
                            message: 'Please input a valid id from 1 to 9999',
                        }
                    ]}
                    label="SellerAccountId"
                    tooltip={'SellerAccountId of the publisher'}
                >
                    <InputNumber style={{ width: '100%' }} controls={false}  disabled/>
                </FormItem>
                <FormItem
                    name="language"
                    label="Language"
                    tooltip={'Publisher language'}
                    rules={[
                        {
                            max: 255,
                            message: 'Language cannot be longer that 255 characters'
                        }
                    ]}
                >
                    <Select
                        optionFilterProp="title"
                        showSearch
                        allowClear
                        placeholder="Select language"
                    >
                        {info.languages.map((item: LanguageBean) => (
                            <Option title={item.name} key={item.id}
                                value={item.id as string}>{item.name}</Option>))}
                    </Select>
                </FormItem>
                <FormItem
                    name="iabCategoriesIds"
                    label="Publisher IAB Categories"
                    tooltip={'IAB categories publisher wants to work with'}
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        style={{ maxHeight: '400px', overflow: 'auto' }}
                        placement={'topRight'}
                        placeholder="Select IAB categories"
                        onChange={onChangeIab}
                        treeData={getIabCategoriesAsTree(info.iabCategories)}
                    />
                </FormItem>
                <FormItem
                    name="dspBlackListIds"
                    label="Dsp blacklist"
                    getValueFromEvent={handleSelectAll}
                    tooltip={'Dsps to do not get ads from'}
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select dsp"
                    >
                        {info.dspList.length > 0 && <Option value={'__all'}>{'Select all dsps'}</Option>}
                        {info.dspList.map((item: DspBean) => (
                            <Option key={item.id} value={item.id as number}>{item.name}</Option>
                        ))}
                    </Select>
                </FormItem>
                <FormItem
                    name="maxQps"
                    label="QPS"
                    tooltip={'Maximum queries per second allowed'}
                    rules={[
                        {
                            pattern: /^\d*$/,
                            message: 'Please input a valid number',
                        }
                    ]}
                >
                    <Input />
                </FormItem>
                {/* <FormItem
                    name="blackListDomainsField"
                    label="Filter Mode"
                    initialValue="white"
                    tooltip={'Choose Domain filter mode'}
                >
                    <Segmented
                        options={[
                            // {
                            //     label: 'Block list',
                            //     value: 'black',
                            //     icon: <StopOutlined />,
                            // },
                            {
                                label: 'Allow list',
                                value: 'white',
                                icon: <CheckCircleOutlined />
                            },
                        ]}
                    />
                </FormItem> */}
                <FormItem
                    label="Allowed Domains"
                    tooltip={'Publisher allowed domains with classifications'}
                    validateStatus={domainMobileError ? 'error' : undefined}
                    help={domainMobileError || undefined}
                >
                    <PublisherDomainListCard
                        title="Web Domains"
                        subtitle="Manage allowlist entries"
                        items={domains}
                        acceptAll={allClassification}
                        collapsed={domainsCollapsed}
                        onToggleCollapse={() => setDomainsCollapsed(v => !v)}
                        onAcceptAllChange={checked => {
                            setAllClassification(checked)
                            if (checked) setDomains([])
                            setDomainMobileError('')
                        }}
                        onAdd={handleAddDomain}
                        onUpload={handleUploadDomains}
                        onRemoveItem={handleRemoveDomain}
                        onClearAll={handleClearAllDomains}
                        initialFileLabel={domainsFileLabel}
                    />
                </FormItem>

                <FormItem
                    name="includeDomains"
                    label="Include Allowed Domains in sellers.JSON"
                    valuePropName="checked"
                >
                    <Checkbox
                        checked={includeDomains}
                        onChange={handleIncludeDomains}
                    >
                    </Checkbox>
                </FormItem>

                <FormItem
                    label="Allowed Mobile App IDs"
                    tooltip={'Publisher allowed mobile app IDs with classifications'}
                    validateStatus={domainMobileError ? 'error' : undefined}
                    help={domainMobileError || undefined}
                >
                    <PublisherDomainListCard
                        title="MobileApp ID"
                        subtitle="Manage allowlist entries"
                        items={mobileAppIds}
                        acceptAll={acceptAllMobileAppIds}
                        collapsed={mobileAppsCollapsed}
                        onToggleCollapse={() => setMobileAppsCollapsed(v => !v)}
                        onAcceptAllChange={checked => {
                            setAcceptAllMobileAppIds(checked)
                            if (checked) setMobileAppIds([])
                            setDomainMobileError('')
                        }}
                        onAdd={handleAddMobileAppId}
                        onUpload={handleUploadMobileAppIds}
                        onRemoveItem={handleRemoveMobileAppId}
                        onClearAll={handleClearAllMobileAppIds}
                        initialFileLabel={mobileAppIdsFileLabel}
                    />
                </FormItem>

                <FormItem
                    name="winNotification"
                    label="Win notification"
                    initialValue="Nurl"
                >
                    <Select
                        allowClear
                        placeholder="Select win notification type">
                        {Object.values(WinNotificationEnum).map(win =>
                            <Option key={win} value={win}>{win}</Option>)}
                    </Select>
                </FormItem>

                <FormItem
                    name="bundle"
                    rules={[
                        {
                            max: 255,
                            message: 'Bundle cannot be longer that 255 characters'
                        }
                    ]}
                    label="Bundle"
                    tooltip={'Publisher\'s App bundle'}
                >
                    <Input />
                </FormItem>
                <FormItem
                    name="storeUrl"
                    rules={[
                        {
                            max: 255,
                            message: 'Store URL cannot be longer that 255 characters'
                        }
                    ]}
                    label="Store URL"
                    tooltip={'Publisher\'s App store URL'}
                >
                    <Input />
                </FormItem>
                {/* <FormItem
                    name="publisherDomain"
                    rules={[
                        {
                            max: 255,
                            message: 'Store URL cannot be longer that 255 characters'
                        }
                    ]}
                    label="Publisher domain"
                    tooltip={'Publisher domain'}
                >
                    <Input />
                </FormItem> */}
                <FormItem
                    name="auctionMode"
                    label="Auction Mode"
                    rules={[{ required: true, message: 'Please select Auction type' }]}
                    tooltip={'Auction type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}
                >
                    <Select placeholder="Select Auction Mode">
                        {Object
                            .keys(PublisherBean.AuctionModeEnum)
                            .map((mode) => <Option key={mode}
                                value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="houseBidderMode"
                    label="House Bidder Mode"
                    rules={[{ required: true, message: 'Please select House Bidder Mode' }]}
                    tooltip={'House Bidder Mode'}
                >
                    <Select placeholder="Select House Bidder Mode">
                        {Object
                            .keys(PublisherBean.HouseBidderModeEnum)
                            .map((mode) => <Option key={mode}
                                value={mode}>{mode.replace(/([a-z])([A-Z])/g, '$1 $2')}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    label="Token"
                    tooltip={'Token identifying publisher on the exchage. Must be inserted in html page'}
                    rules={[
                        {
                            max: 255,
                            message: 'Token cannot be longer that 255 characters'
                        }
                    ]}
                >
                    <Input value={token} addonAfter={(
                        <Button style={{ border: 0, backgroundColor: '#fafafa' }} size={'small'} htmlType="button"
                            onClick={handleTokenGenerator}>
                            Generate
                        </Button>
                    )} />
                </FormItem>
                <FormItem
                    required
                    label="Alkimi Exchange Fee"
                    tooltip="Publisher Fee"
                >
                    <FormItem
                        name="fee"
                        rules={[{ required: true, message: 'Fee is required' }]}
                        className="ant-item ant-item-first"
                    >
                        <InputNumber step={0.01} min={0} max={99} />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">%</FormItem>
                </FormItem>
                <FormItem
                    name="sellerDomain"
                    label="Seller Domain"
                    rules={[
                        {
                            required: true,
                            message: 'Please input Seller Domain'
                        }
                    ]}
                    tooltip={'Seller domains (domain1)'}
                >
                    <Input />

                </FormItem>
                <FormItem
                    name="sellerType"
                    label="Seller Type"
                    rules={[{ required: true, message: 'Please select Seller type' }]}
                    // tooltip={'Seller type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}
                >
                    <Select placeholder="Select Seller type" >
                            {SellerType.map(element=><Option key={element.value} value={element.value}>{element.label}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="revenueCalculationMethod"
                    label="Revenue Calculation Method"
                    rules={[{ required: true, message: 'Please select Revenue Calculation Method' }]}
                    // tooltip={'Seller type, where 1 = First Price, 2 = Second Price Plus. Exchange-specific auction types can be defined using values greater than 500'}
                >
                    <Select placeholder="Select Revenue Calculation Method" >
                            {RevenueCalculationMethod.map(element=><Option key={element.value} value={element.value}>{element.label}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="passThrough"
                    label="Pass Through"
                    valuePropName="checked"
                    // tooltip={'If true publisher should send signed request only'}
                >
                    <Checkbox
                        onChange={handlepassThrough}
                    />
                </FormItem>
                <FormItem
                    name="useParentSellerAccountId"
                    label="Use Parent Seller Id"
                    valuePropName="checked"
                    // tooltip={'If true publisher should send signed request only'}
                >
                    <Checkbox
                        onChange={handleuseParentSellerAccountId}
                        // disabled={!parentPublisherId}
                    />
                </FormItem>
                <FormItem
                    name="parentPublisherId"
                    label="Parent Publisher"
                    rules={useParentSellerAccountId
                        ? [
                            {
                              required: true,
                              message: 'Please select parent publisher id'
                            }
                          ]
                        : []
                      }

                    // getValueFromEvent={handleSelectAll}
                    tooltip={'Select Parent Publisher Id'}
                >
                    <Select
                        showSearch
                        allowClear
                        // mode="multiple"
                        onChange={handleParentPublisherId}
                        placeholder="Select parent publisher id"
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase()?.includes(input?.toLowerCase())
                        }
                    >
                        {/* {info.dspList.length > 0 && <Option value={'__all'}>{'Select all dsps'}</Option>} */}
                        {info?.publishers?.map((item: DspBean) => {
                            return<> {item?.id != id ? <Option key={item.id} value={item.id as number}>{item.name}</Option>:<></>}</>}
                        )}

                    </Select>
                </FormItem>


                <FormItem
                    name="validation"
                    label="Validation"
                    valuePropName="checked"
                    tooltip={'If true publisher should send signed request only'}
                >
                    <Checkbox
                        onChange={handleValidation}
                    />
                </FormItem>
                <FormItem
                    name="publicValidationKey"
                    label="Public validation key"
                    rules={[
                        {
                            max: 255,
                            message: 'Public validation key cannot be longer that 255 characters'
                        }
                    ]}
                    tooltip={'Public validation key need to validate publisher request'}
                >
                    <Input disabled={!validation} />
                </FormItem>
                {/* <FormItem
                    name="active"
                    label="Active"
                    valuePropName="checked"
                    tooltip={'If true publisher can send requests to exchange'}
                >
                    <Checkbox />
                </FormItem> */}
                <FormItem
                    name="email"
                    rules={[
                        { required: true, message: 'Please input Publisher email' },
                        {
                            pattern: new RegExp('.+@.+\\..+'),
                            message: 'Email is incorrect'
                        }
                    ]}
                    label="Email"
                >
                    <Input />
                </FormItem>
                <FormItem
                    name="timezoneId"
                    rules={[{ required: true, message: 'Please select Time Zone', type: 'string' }]}
                    label="Time Zone"
                >
                    <Select
                        showSearch
                        placeholder="Select time zone">
                        {info.timezones.map((timezone: TimeZoneBean) =>
                            <Option key={timezone.id} value={timezone.id as string}>{timezone.id}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="test"
                    label="Restrict Blockchain Transaction"
                    valuePropName="checked"
                    tooltip="If true publisher transactions will not be sent to Blockchain ledger"
                >
                    <Checkbox />
                </FormItem>
                <FormItem
                    required
                    label="Validations limit"
                    tooltip="Limit the number of validation report"
                >
                    <FormItem
                        name="athenaValidationsLimit"
                        rules={[{ required: true, message: 'Limit is required' }]}
                        className="ant-item ant-item-first"
                    >
                        <InputNumber min={0} defaultValue={0} />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">Validations remaining in the current period: {form.getFieldValue('athenaValidationsLeft')}</FormItem>
                </FormItem>
                <FormItem
                    required
                    label="Request/Response Id period limit"
                    tooltip="Limit of period for Request/Response Id report in hours"
                >
                    <FormItem
                        name="athenaPeriodLimit"
                        rules={[{ required: true, message: 'Limit is required' }]}
                        className="ant-item ant-item-first"
                    >
                        <InputNumber min={0} defaultValue={0} />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">Report period remaining in the current period: {form.getFieldValue('athenaPeriodLeft')}</FormItem>
                    <FormItem className="ant-item ant-item-second">hours</FormItem>
                </FormItem>
                <FormItem
                    name="countries"
                    label="Countries"
                    tooltip="Countries"
                >
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder="Select Countries"
                        optionFilterProp="title"
                        onSelect={handleCountrySelect}
                        onDeselect={handleCountryDeselect}
                    >
                        <Option key="All" title="All" value="All">All</Option>
                        {info.countries.map((country: CountryBean) => (
                            <Option key={country.id} title={country.name} value={country.id as number}>{country.name}</Option>
                        ))}
                    </Select>
                </FormItem>
            </Form>
            <Form
                style={{
                    maxWidth: '1000px'
                }}
                onFinish={handleSubmit}>
                <FormItem
                    wrapperCol={{
                        offset: 6, span: 18
                    }}> {status === 'DRAFT'  && (
                        <Button
                          style={{ width: '24%', margin: '0 1% 1% 0', background: '#10b981b8', color: '#fff' }}
                          htmlType="submit"
                          onClick={() => setSaveMode('DRAFT')}
                        >
                          Save as Draft
                        </Button>
                      )}
                    <Button style={{
                        width: '24%',
                        margin: '0 0 0 0'
                    }} type="primary" htmlType="submit" onClick={() => setSaveMode('SAVE')}>{status === 'DRAFT' ? 'Activate' : 'Save'}</Button>
                    <Button style={{
                        width: '24%',
                        margin: '0 0 0 1%'
                    }} type="primary" danger htmlType="button" onClick={() => navigateToPublisherList()}>Cancel</Button>
                    <Button style={{
                        width: '24%',
                        margin: '0 0 0 1%'
                    }} type="default" htmlType="button" onClick={generateCode}>Generate JS Tag</Button>
                </FormItem>
            </Form>
        </div>
    )
}

export default PublisherEdit